import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import BookingModal from '../../components/BookingModal';

const ChimpKyamburaGorge = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Kyambura Gorge Chimp Tracking
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Track chimpanzees in the dramatic "Valley of Apes" within Queen Elizabeth National Park
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(4)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                  <FontAwesomeIcon icon="fa-star-half-stroke" className="w-5 h-5" />
                </div>
                <span className="text-lg">(134 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-map" className="w-4 h-4 mr-2" />
                Underground Forest
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                60-70% Success Rate
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Chimp Tracking
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Descend Into the Valley of Apes
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Kyambura Gorge is one of Uganda's most dramatic primate tracking destinations—a lush, 100-meter-deep rift valley that slices through Queen Elizabeth National Park's savannah, creating an isolated "underground forest" that shelters a habituated chimpanzee community. Locals call it the "Valley of Apes," and descending into its depths feels like entering a lost world where the echoing pant-hoots of chimps reverberate off steep walls.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Unlike the extensive forests of Kibale where chimps roam vast territories, Kyambura's chimps are confined to this 11-kilometer-long gorge, making tracking more challenging but also more intimate. The success rate hovers around 60-70%, lower than Kibale's 95%, but the unique gorge environment and the adventure of descending into this hidden valley make the experience special regardless of whether chimps are spotted.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your tracking begins at the gorge rim where ranger guides provide briefings. The initial descent is immediately adventurous—you scramble down steep, narrow paths that zigzag down the gorge wall. Within minutes, you transition from open savannah to dense riverine forest where temperatures drop, humidity rises, and the soundscape transforms from birdsong to the rushing of the Kyambura River.
                </p>
                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">The Tracking Experience</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Once on the gorge floor, the tracking begins in earnest. Rangers use radio communication with scouts who monitor chimp movements from the rim. The Kyambura community comprises roughly 20-25 individuals. Sometimes you find them within 30 minutes; other times you search for hours, following calls and fresh signs through dense vegetation and across the river via log bridges.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  When you do locate the chimps, the experience is thrilling. You might watch a mother tenderly grooming her infant, juvenile males rough-housing, or the dominant alpha male assert authority. You are allowed one hour with located chimps, similar to gorilla trekking protocols.
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                  <p className="text-gray-700">
                    <strong>Important Note:</strong> The 60-70% success rate means roughly one in three trips returns without finding chimps. However, you will still experience the gorgeous gorge environment, see other primates (colobus, red-tailed monkeys), and enjoy a genuine forest adventure.
                  </p>
                </div>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Experience Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-map', title: 'Dramatic Gorge Setting', description: '100m-deep rift valley with isolated "underground forest" ecosystem' },
                  { icon: 'fa-users', title: 'Habituated Chimp Community', description: '20-25 individuals accustomed to human presence for close observations' },
                  { icon: 'fa-mountain', title: 'Adventure Descent', description: 'Steep scramble down gorge walls into hidden valley—thrilling approach' },
                  { icon: 'fa-leaf', title: 'Riverine Forest Habitat', description: 'Lush microclimate with fig trees, wild palms, and dense undergrowth' },
                  { icon: 'fa-eye', title: 'Other Primate Species', description: 'Colobus, red-tailed monkeys, baboons, and blue monkeys also present' },
                  { icon: 'fa-music', title: 'Chimp Vocalizations', description: 'Hear famous pant-hoots echoing through gorge—spine-tingling sound' },
                  { icon: 'fa-dollar-sign', title: 'Affordable Permit', description: '$50 vs $250 Kibale—excellent value for primate tracking' },
                  { icon: 'fa-camera', title: 'Unique Photography', description: 'Gorge landscapes and chimp behavior in intimate forest setting' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Trek Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences.
                  We can adjust timing and combine with other Queen Elizabeth activities.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '08:00 AM', title: 'Briefing', desc: 'Gorge rim orientation, chimp behavior, safety protocols' },
                  { time: '08:30 AM', title: 'Gorge Descent', desc: 'Steep scramble down 100m walls into valley floor—adventurous!' },
                  { time: '09:00 AM', title: 'Tracking Begins', desc: 'Follow ranger leads through riverine forest searching for chimp signs' },
                  { time: '10:30 AM', title: 'Chimp Located', desc: 'Find community feeding or resting—begin 1-hour observation period' },
                  { time: '11:30 AM', title: 'Observation Ends', desc: 'Leave chimps and begin ascent back to gorge rim' },
                  { time: '12:00 PM', title: 'Return', desc: 'Exit gorge breathless but exhilarated, receive tracking certificate' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time.split(' ')[0]}<br />{step.time.split(' ')[1]}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Common Questions
              </h2>
              <div className="space-y-6">
                {[
                  { q: 'Why is success rate lower than Kibale?', a: 'Kyambura chimps are confined to an 11km gorge, but it is still a large area with dense vegetation. They move unpredictably and sometimes venture outside tracking zones. The isolated population also means fewer individuals to locate. However, the unique gorge setting and lower permit cost ($50 vs $250) make it worthwhile even with lower odds.' },
                  { q: 'What happens if we don\'t find chimps?', a: 'You still experience the beautiful gorge descent, see other primates (colobus, red-tailed monkeys, baboons), birds, and forest scenery. Rangers make genuine efforts to locate chimps, but cannot guarantee sightings. No refund is given—the permit covers the tracking attempt and conservation support, not guaranteed sightings.' },
                  { q: 'How physically demanding is it?', a: 'Moderate to strenuous. The steep gorge descent and especially the ascent require good fitness and balance. Forest tracking involves uneven terrain, river crossings, and dense vegetation. If you can hike moderately difficult trails with elevation changes, you will manage. Porters available to carry daypacks.' },
                  { q: 'How does it compare to Kibale chimp tracking?', a: 'Kyambura: Dramatic gorge, adventurous, 60-70% success, $50 permit, smaller groups, more challenging terrain. Kibale: Extensive forest, 95%+ success, $250 permit, easier walking, longer observation opportunities. Both habituated chimps. Choice depends on budget, certainty preference, and adventure appetite.' }
                ].map((faq, index) => (
                  <div key={index} className="border-l-4 border-safari-gold pl-6 py-4">
                    <h3 className="text-xl font-bold text-safari-green mb-2">{faq.q}</h3>
                    <p className="text-gray-700">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Queen Elizabeth Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/kazinga-boat-safari" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1576485290814-1c72aa4bbb8e?w=800"
                      alt="Kazinga Channel"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Kazinga Boat Safari</h3>
                    <p className="text-gray-700 mb-4">See hippos, elephants, and crocs from the water.</p>
                    <p className="text-safari-gold font-bold">From $30 per person</p>
                  </div>
                </Link>

                <Link to="/activities/lion-tracking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1546182990-dffeafbe841d?w=800"
                      alt="Lion Tracking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Lion Tracking</h3>
                    <p className="text-gray-700 mb-4">Join researchers tracking tree-climbing lions.</p>
                    <p className="text-safari-gold font-bold">From $240 per person</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 3-5 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Price:</strong> $50 per person</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate-Strenuous</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Max 6 people</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 12 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Tracking
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Tracking permit ($50)",
                    "Park entrance fees",
                    "Ranger guide",
                    "1 hour with chimps (if found)",
                    "Tracking certificate"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to Queen Elizabeth NP",
                    "Accommodation",
                    "Meals",
                    "Porter hire (recommended)",
                    "Tips"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate. Steep descent/ascent.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> No infectious diseases allowed.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Kyambura Gorge Chimp Tracking"
      />
    </div>
  );
};

export default ChimpKyamburaGorge;
